import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { TooltipModule } from 'primeng/tooltip';
import { CustomersByHabitWidget, HabitMetricUnit } from '../../models/loyalty-dashboard.model';

@Component({
  selector: 'app-customers-by-habit-widget',
  standalone: true,
  imports: [CommonModule, PanelModule, TooltipModule],
  template: `
    <p-panel header="Returning customers frequency">
      <div class="habit" *ngIf="data as widget; else noData">
        <div class="habit__table-wrap">
          <table class="habit__table">
            <thead>
              <tr>
                <th class="habit__col-segment">Type</th>
                <th class="habit__col-data">Members</th>
                <th class="habit__col-data">Non-members</th>
              </tr>
            </thead>
            <tbody>
              <tr class="habit__row" *ngFor="let segment of widget.segments">
                <td class="habit__segment">
                  <span class="habit__label">{{ segment.label }}</span>
                  <i
                    *ngIf="hasInfoTooltip(segment.label); else inlineDescription"
                    class="pi pi-info-circle habit__info"
                    [pTooltip]="segment.description"
                    tooltipPosition="top"
                  ></i>
                  <ng-template #inlineDescription
                    ><span class="habit__description">({{ segment.description }})</span></ng-template
                  >
                </td>
                <td class="habit__col-data habit__member">
                  <span class="habit__pair">
                    <span class="habit__qty">{{ segment.memberCustomers | number }}</span>
                    <span class="habit__metric-col">
                      <span
                        *ngIf="isDirectional(segment.unit) && segment.member !== null"
                        class="habit__metric"
                        [class.habit__metric--up]="segment.member > 0"
                        [class.habit__metric--down]="segment.member < 0"
                        ><span class="habit__arrow">{{ arrow(segment.member) }}</span
                        >{{ format(segment.member, segment.unit) }}</span
                      >
                    </span>
                    <ng-container *ngIf="stacksMetric(segment.unit) && segment.member !== null">
                      <span class="habit__qty habit__metric">{{ segment.member | number }}%</span>
                      <span class="habit__metric-col"><span class="habit__metric">returned</span></span>
                    </ng-container>
                  </span>
                </td>
                <td class="habit__col-data habit__non-member">
                  <span class="habit__pair">
                    <span class="habit__qty">{{ segment.nonMemberCustomers | number }}</span>
                    <span class="habit__metric-col">
                      <span
                        *ngIf="isDirectional(segment.unit) && segment.nonMember !== null"
                        class="habit__metric"
                        [class.habit__metric--up]="segment.nonMember > 0"
                        [class.habit__metric--down]="segment.nonMember < 0"
                        ><span class="habit__arrow">{{ arrow(segment.nonMember) }}</span
                        >{{ format(segment.nonMember, segment.unit) }}</span
                      >
                    </span>
                    <ng-container *ngIf="stacksMetric(segment.unit) && segment.nonMember !== null">
                      <span class="habit__qty habit__metric">{{ segment.nonMember | number }}%</span>
                      <span class="habit__metric-col"><span class="habit__metric">returned</span></span>
                    </ng-container>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <ng-template #noData>
        <p class="habit__empty">No data available.</p>
      </ng-template>
    </p-panel>
  `,
  styles: [
    `
      .habit {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }
      .habit__subtitle {
        margin: 0;
        font-size: 0.9rem;
        opacity: 0.65;
      }
      .habit__table-wrap {
        overflow-x: auto;
      }
      .habit__table {
        width: 100%;
        border-collapse: collapse;
      }
      .habit__table th {
        font-weight: 400;
        font-size: 0.85rem;
        opacity: 0.65;
        padding: 0 0 0.75rem;
        border-bottom: 1px solid var(--surface-200, #e9ecef);
        text-align: right;
      }
      .habit__col-segment {
        text-align: left !important;
        width: 100%;
      }
      .habit__col-data {
        text-align: center !important;
        white-space: nowrap;
      }
      .habit__table th.habit__col-data,
      .habit__row td.habit__col-data {
        padding-left: 2.5rem;
      }
      .habit__pair {
        display: inline-grid;
        grid-template-columns: 3.25rem 6.25rem;
        column-gap: 0.6rem;
        row-gap: 0.25rem;
        text-align: left;
        white-space: nowrap;
      }
      .habit__qty {
        text-align: right;
      }
      .habit__metric-col {
        text-align: left;
      }
      .habit__metric {
        font-weight: 400;
        opacity: 0.6;
      }
      .habit__arrow {
        font-weight: 700;
        margin-right: 0.1rem;
      }
      .habit__metric--up {
        color: #16a34a;
        opacity: 1;
      }
      .habit__metric--down {
        color: #dc2626;
        opacity: 1;
      }
      .habit__row td {
        padding: 0.9rem 0;
        border-bottom: 1px solid var(--surface-200, #e9ecef);
        vertical-align: top;
      }
      .habit__row:last-child td {
        border-bottom: none;
      }
      .habit__segment {
        white-space: normal;
      }
      .habit__label {
        font-weight: 700;
      }
      .habit__description {
        margin-left: 0.35rem;
        font-size: 0.85rem;
        opacity: 0.65;
      }
      .habit__info {
        margin-left: 0.4rem;
        font-size: 0.8rem;
        opacity: 0.45;
        cursor: help;
        vertical-align: baseline;
      }
      .habit__info:hover {
        opacity: 0.8;
      }
      @media (max-width: 768px) {
        .habit__info {
          display: none;
        }
        .habit__table {
          width: auto;
        }
        .habit__col-segment {
          width: auto;
          padding-right: 1.25rem;
        }
        .habit__table th.habit__col-data,
        .habit__row td.habit__col-data {
          padding-left: 1rem;
        }
        .habit__col-data {
          text-align: left !important;
        }
      }
      .habit__member {
        font-weight: 700;
        color: var(--primary-color, #2563eb);
      }
      .habit__non-member {
        opacity: 0.7;
      }
      .habit__empty {
        opacity: 0.6;
      }
    `,
  ],
})
export class CustomersByHabitWidgetComponent {
  @Input() data: CustomersByHabitWidget | null = null;

  private readonly tooltipSegments = new Set(['occasionals', 'came back', 'new']);

  hasInfoTooltip(label: string): boolean {
    return this.tooltipSegments.has(label.trim().toLowerCase());
  }

  isDirectional(unit: HabitMetricUnit): boolean {
    return unit === 'percentChange' || unit === 'visitsDelta';
  }

  arrow(value: number): string {
    if (value > 0) {
      return '⬆︎';
    }
    if (value < 0) {
      return '⬇︎';
    }
    return '';
  }

  format(value: number | null, unit: HabitMetricUnit): string {
    if (value === null || value === undefined) {
      return '—';
    }
    switch (unit) {
      case 'percentChange':
        return `${Math.abs(value)}%`;
      case 'visitsDelta':
        return `${Math.abs(value).toFixed(1)} visits`;
      case 'percentReturned':
        return `${value}% returned`;
    }
  }

  stacksMetric(unit: HabitMetricUnit): boolean {
    return unit === 'percentReturned';
  }
}
